import { useState } from "react";
import {
  ChevronDown,
  Building2,
  Users,
  Crown,
  Plus,
  Share2,
} from "lucide-react";
import { useStartupContext } from "@/hooks/useStartupContext";
import { useProAccess } from "@/hooks/useProAccess";
import { FREE_TIER_MAX_OWNED_STARTUPS } from "@/constants/pro-features";
import type { Startup } from "@/types/startup.types";
import { useAuth } from "@/hooks/useAuth.hook";

interface StartupSelectorProps {
  className?: string;
}

function StartupListItem({
  startup,
  isSelected,
  firebaseUserId,
  onSelect,
}: {
  startup: Startup;
  isSelected: boolean;
  firebaseUserId: string;
  onSelect: () => void;
}) {
  const isOwner = startup.ownerId === firebaseUserId;
  const member = startup.members?.find((m) => m.userId === firebaseUserId);
  const role = isOwner ? "Owner" : member?.role || "Member";
  const memberCount = startup.members?.length || 1;

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`mx-2 w-[calc(100%-1rem)] rounded-[14px] px-3 py-2.5 text-left text-sm transition-colors ${
        isSelected
          ? "bg-brand/15 text-white ring-1 ring-brand/30"
          : "text-white/80 hover:bg-surface/5"
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="truncate font-semibold">{startup.name}</span>
        {isOwner ? (
          <Crown className="h-3 w-3 shrink-0 text-amber-400" />
        ) : (
          <Share2 className="h-3 w-3 shrink-0 text-sky-400" />
        )}
      </div>
      {startup.description ? (
        <div className="mt-0.5 truncate text-xs text-white/45">
          {startup.description}
        </div>
      ) : null}
      {!isOwner && startup.ownerDisplayName ? (
        <div className="mt-0.5 truncate text-xs text-white/45">
          Shared by {startup.ownerDisplayName}
        </div>
      ) : null}
      <div className="mt-1 flex items-center gap-3 text-xs text-white/45">
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          {memberCount} member{memberCount !== 1 ? "s" : ""}
        </span>
        <span className="rounded bg-surface/10 px-1.5 py-0.5 capitalize">
          {role}
        </span>
      </div>
    </button>
  );
}

export function StartupSelector({ className = "" }: StartupSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { firebaseUser } = useAuth();
  const { isPro } = useProAccess();
  const {
    currentStartup,
    ownedStartups,
    sharedStartups,
    availableStartups,
    selectStartup,
    openCreateStartupModal,
    isLoading,
  } = useStartupContext();

  if (!firebaseUser || isLoading) {
    return null;
  }

  const totalCount = availableStartups.length;
  const needsProForMore =
    ownedStartups.length >= FREE_TIER_MAX_OWNED_STARTUPS && !isPro;
  const currentIsOwner =
    currentStartup && currentStartup.ownerId === firebaseUser.uid;

  return (
    <div className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-[14px] border border-white/10 bg-surface-elevated px-3 py-2 text-sm text-white hover:bg-surface/10 focus:outline-none focus:ring-2 focus:ring-brand/40"
      >
        <Building2 className="h-4 w-4 text-white/50" />
        <span className="max-w-[140px] truncate font-semibold">
          {currentStartup?.name || (totalCount === 0 ? "No startup" : "Select startup")}
        </span>
        {currentIsOwner ? (
          <span title="Owner">
            <Crown className="h-3 w-3 text-amber-400" />
          </span>
        ) : currentStartup ? (
          <span title="Shared">
            <Share2 className="h-3 w-3 text-sky-400" />
          </span>
        ) : null}
        <ChevronDown
          className={`h-4 w-4 text-white/40 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen ? (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
            aria-hidden
          />
          <div className="absolute right-0 z-20 mt-1 w-80 rounded-[20px] bg-surface py-2 ring-1 ring-white/[0.09] shadow-glow">
            {totalCount > 0 ? (
              <div className="px-3 pb-2 text-xs font-semibold text-white/45">
                Your startups ({totalCount})
              </div>
            ) : (
              <div className="px-3 pb-2 text-xs text-white/45">
                No startups yet — create one to get started.
              </div>
            )}

            {ownedStartups.length > 0 ? (
              <div className="pb-1">
                <div className="px-4 py-1 text-[10px] font-black uppercase tracking-wide text-white/35">
                  My startups
                </div>
                {ownedStartups.map((startup) => (
                  <StartupListItem
                    key={startup.id}
                    startup={startup}
                    isSelected={currentStartup?.id === startup.id}
                    firebaseUserId={firebaseUser.uid}
                    onSelect={() => {
                      selectStartup(startup.id);
                      setIsOpen(false);
                    }}
                  />
                ))}
              </div>
            ) : null}

            {sharedStartups.length > 0 ? (
              <div className="pb-1">
                <div className="px-4 py-1 text-[10px] font-black uppercase tracking-wide text-white/35">
                  Shared with me
                </div>
                {sharedStartups.map((startup) => (
                  <StartupListItem
                    key={startup.id}
                    startup={startup}
                    isSelected={currentStartup?.id === startup.id}
                    firebaseUserId={firebaseUser.uid}
                    onSelect={() => {
                      selectStartup(startup.id);
                      setIsOpen(false);
                    }}
                  />
                ))}
              </div>
            ) : null}

            <div className="mx-2 mt-1 border-t border-white/10 pt-1">
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  openCreateStartupModal();
                }}
                className="flex w-full items-center justify-between gap-2 rounded-[14px] px-3 py-2.5 text-left text-sm font-semibold text-brand hover:bg-brand/10"
              >
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Create startup
                </span>
                {needsProForMore ? (
                  <span className="rounded-full bg-brand/20 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-brand">
                    Pro
                  </span>
                ) : null}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
